import { useEffect, useRef, type RefObject } from "react";

export function useIOSKeyboard(
  scrollRef: RefObject<HTMLDivElement | null>,
  inputAreaRef: RefObject<HTMLDivElement | null>,
  textareaRef: RefObject<HTMLTextAreaElement | null>,
) {
  const touchRef = useRef<{
    el: EventTarget | null;
    x: number;
    y: number;
    dragDismissed: boolean;
  }>({ el: null, x: 0, y: 0, dragDismissed: false });

  useEffect(() => {
    const scrollEl = scrollRef.current;
    const inputAreaEl = inputAreaRef.current;
    const textareaEl = textareaRef.current;
    if (!scrollEl || !inputAreaEl || !textareaEl) return;

    // Capture non-null refs for use in closures (TS doesn't narrow into closures)
    const _scroll: HTMLDivElement = scrollEl;
    const _inputArea: HTMLDivElement = inputAreaEl;
    const _textarea: HTMLTextAreaElement = textareaEl;

    let vvHandler: (() => void) | null = null;

    if (window.visualViewport) {
      vvHandler = () => {
        const vv = window.visualViewport!;
        window.scrollTo(0, 0);

        const fullHeight = window.innerHeight;
        const kbHeight = fullHeight - vv.height;

        if (kbHeight > 50) {
          _inputArea.style.position = "fixed";
          _inputArea.style.bottom = `${kbHeight}px`;
          _inputArea.style.left = "0";
          _inputArea.style.right = "0";
          _scroll.style.paddingBottom = `${kbHeight + 80}px`;
        } else {
          _inputArea.style.position = "";
          _inputArea.style.bottom = "";
          _inputArea.style.left = "";
          _inputArea.style.right = "";
          _scroll.style.paddingBottom = "";
        }

        requestAnimationFrame(() => {
          _scroll.scrollTop = _scroll.scrollHeight;
        });
      };

      window.visualViewport.addEventListener("resize", vvHandler);
    }

    function onTouchStart(e: TouchEvent) {
      touchRef.current = {
        el: e.target,
        x: e.touches[0]!.clientX,
        y: e.touches[0]!.clientY,
        dragDismissed: false,
      };
    }

    function onTouchMove(e: TouchEvent) {
      const t = touchRef.current;
      if (t.dragDismissed || document.activeElement !== _textarea) return;

      const target = t.el as HTMLElement | null;
      if (!target || !_inputArea.contains(target)) return;
      if (target === _textarea || target.closest("textarea")) return;

      const dy = e.touches[0]!.clientY - t.y;
      const dx = Math.abs(e.touches[0]!.clientX - t.x);
      if (dy > 10 && dy > dx) {
        t.dragDismissed = true;
        _textarea.blur();
      }
    }

    function onTouchEnd(e: TouchEvent) {
      const t = touchRef.current;
      if (t.dragDismissed || document.activeElement !== _textarea) return;

      const dy = Math.abs(e.changedTouches[0]!.clientY - t.y);
      const target = t.el as HTMLElement | null;

      if (
        dy < 10 &&
        target &&
        _scroll.contains(target) &&
        !target.closest("button") &&
        !target.closest("a") &&
        !target.closest("textarea")
      ) {
        _textarea.blur();
      }
    }

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd);

    return () => {
      if (vvHandler && window.visualViewport) {
        window.visualViewport.removeEventListener("resize", vvHandler);
      }
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);

      _inputArea.style.position = "";
      _inputArea.style.bottom = "";
      _inputArea.style.left = "";
      _inputArea.style.right = "";
      _scroll.style.paddingBottom = "";
    };
  }, [scrollRef, inputAreaRef, textareaRef]);
}
